<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import ImpressoesService from '@/services/impressoesService'
import type { ImpressaoEstatisticas, SerieTemporal } from '@/services/impressoesService'
import AcoesService from '@/services/acoesService'
import type { EstatisticasEngajamento, FunilPorTermo } from '@/services/acoesService'
import { VCard, VCardText, VCardTitle, VRow, VCol, VDataTable, VAutocomplete } from 'vuetify/components'
import VueApexCharts from 'vue3-apexcharts'
import moment from 'moment'
import type { IFornecedor } from '@/pages/admin/fornecedor/types'
import type { IUser } from '@/pages/admin/users/types'
import useApi from '@/composables/useApi'

definePage({
  meta: {
    subject: 'fornecedor',
    action: 'read',
  },
})

// Obtém o fornecedor atual e dados do usuário
const currentSupplier = useCookie<IFornecedor>('currentSupplier')
const userData = useCookie<IUser>('userData')

// Lista de fornecedores disponíveis (para admin)
const fornecedores = ref<IFornecedor[]>([])
const fornecedorSelecionado = ref<IFornecedor | null>(null)
const loadingFornecedores = ref(false)

// Verifica se o usuário é admin
const isAdmin = computed(() => {
  const abilities = (userData.value as any)?.abilities || []
  // Verifica se tem permissão de admin ou se pode listar fornecedores
  return abilities.some((a: any) => 
    a.subject === 'all' || 
    (a.action === 'list' && a.subject === 'fornecedor')
  )
})

const fornecedorId = computed(() => {
  console.log('🔍 [Dashboard] Calculando fornecedorId:', {
    fornecedorSelecionado: fornecedorSelecionado.value?.id,
    currentSupplier: currentSupplier.value?.id,
    userData_supplier: userData.value?.supplier_selected_id,
  })
  
  // Se houver fornecedor selecionado (admin escolhendo), usa ele
  if (fornecedorSelecionado.value?.id) {
    console.log('✅ [Dashboard] Usando fornecedorSelecionado:', fornecedorSelecionado.value.id)
    return fornecedorSelecionado.value.id
  }
  
  // Tenta primeiro do currentSupplier
  if (currentSupplier.value?.id) {
    console.log('✅ [Dashboard] Usando currentSupplier:', currentSupplier.value.id)
    return currentSupplier.value.id
  }
  
  // Se não encontrar, tenta do userData.supplier_selected_id
  if (userData.value?.supplier_selected_id) {
    console.log('✅ [Dashboard] Usando userData.supplier_selected_id:', userData.value.supplier_selected_id)
    return userData.value.supplier_selected_id
  }
  
  console.log('❌ [Dashboard] Nenhum fornecedor encontrado!')
  return ''
})

// Estados
const loading = ref(false)
const estatisticas = ref<ImpressaoEstatisticas | null>(null)
const serieTemporal = ref<SerieTemporal[]>([])
const estatisticasEngajamento = ref<EstatisticasEngajamento | null>(null)
const funilPorTermo = ref<FunilPorTermo[]>([])

// Filtros de período
const periodoSelecionado = ref<'7dias' | '30dias' | '90dias' | 'personalizado'>('30dias')
const dataInicio = ref<string>('')
const dataFim = ref<string>('')

// Dados para gráfico
const chartOptions = computed(() => ({
  chart: {
    type: 'line',
    height: 350,
    toolbar: {
      show: true,
    },
    zoom: {
      enabled: true,
    },
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  xaxis: {
    categories: serieTemporal.value.map(item => moment(item.periodo).format('DD/MM')),
    title: {
      text: 'Período',
    },
  },
  yaxis: {
    title: {
      text: 'Número de Impressões',
    },
  },
  colors: ['#2a72fc'],
  dataLabels: {
    enabled: false,
  },
  title: {
    text: 'Impressões nos Resultados de Busca',
    align: 'left',
  },
}))

const chartSeries = computed(() => [
  {
    name: 'Impressões',
    data: serieTemporal.value.map(item => item.total),
  },
])

// Opções e dados para o gráfico de funil de conversão
const chartFunilOptions = computed(() => ({
  chart: {
    type: 'line',
    height: 350,
    toolbar: {
      show: true,
    },
    zoom: {
      enabled: true,
    },
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  xaxis: {
    categories: serieTemporal.value.map(item => moment(item.periodo).format('DD/MM')),
    title: {
      text: 'Período',
    },
  },
  yaxis: {
    title: {
      text: 'Quantidade',
    },
  },
  colors: ['#7F56D9', '#12B76A', '#F04438'],
  legend: {
    position: 'top',
  },
  tooltip: {
    shared: true,
    intersect: false,
  },
}))

const chartFunilSeries = computed(() => {
  // Precisamos agrupar as ações por período para comparar com as impressões
  // Por simplicidade, vamos mostrar apenas os totais gerais no gráfico
  return [
    {
      name: 'Impressões',
      data: serieTemporal.value.map(item => item.total),
    },
    {
      name: 'Visualizações',
      data: Array(serieTemporal.value.length).fill(0), // TODO: Implementar série temporal de visualizações
    },
    {
      name: 'Contatos',
      data: Array(serieTemporal.value.length).fill(0), // TODO: Implementar série temporal de contatos
    },
  ]
})

// Headers da tabela de top termos
const headersTermos: Array<{ title: string; key: string; align: 'start' | 'end' | 'center' }> = [
  { title: 'Categoria', key: 'categoria', align: 'start' },
  { title: 'Tipo', key: 'tipo', align: 'start' },
  { title: 'Total', key: 'total', align: 'end' },
]

// Headers da tabela de funil
const headersFunil: Array<{ title: string; key: string; align: 'start' | 'end' | 'center' }> = [
  { title: 'Categoria', key: 'categoria', align: 'start' },
  { title: 'Tipo', key: 'tipo', align: 'start' },
  { title: 'Impressões', key: 'impressoes', align: 'end' },
  { title: 'Visualizações', key: 'visualizacoes', align: 'end' },
  { title: 'Contatos', key: 'contatos', align: 'end' },
  { title: 'Taxa Conv.%', key: 'taxa_conversao', align: 'end' },
]

const mapaTiposFornecedor: Record<string, { label: string; tooltip: string }> = {
  suplicy: {
    label: 'Serviços & Produtos',
    tooltip: 'Fornecedores tradicionais (buffet, decoração, fotografia etc.). Histórico interno identificado como “suplicy”.',
  },
  'space-events': {
    label: 'Espaço para Evento',
    tooltip: 'Locais, salões e espaços físicos disponíveis para realizar eventos.',
  },
  experience: {
    label: 'Experiência',
    tooltip: 'Atividades diferenciadas e experiências oferecidas como parte do evento.',
  },
}

const tooltipTipoResumo = 'Serviços & Produtos: fornecedores tradicionais; Espaço para Evento: locais físicos; Experiência: atividades diferenciadas.'

function obterInfoTipo(tipo?: string) {
  if (!tipo) {
    return {
      label: 'Não informado',
      tooltip: 'Este resultado não possui um tipo de fornecedor definido.',
    }
  }

  return (
    mapaTiposFornecedor[tipo] ?? {
      label: tipo,
      tooltip: 'Tipo de fornecedor não catalogado automaticamente.',
    }
  )
}

const tooltipCategoriaResumo = 'Categoria selecionada na busca (ex.: Alimentos e Bebidas, Decoração, Música ao vivo).'

function normalizarCategoria(slug?: string) {
  if (!slug) {
    return {
      label: 'Não informada',
      tooltip: 'Este resultado não possui uma categoria associada.',
    }
  }

  const label = slug
    .replace(/[-_]+/g, ' ')
    .toLowerCase()
    .replace(/\b\p{L}/gu, letra => letra.toUpperCase())

  return {
    label,
    tooltip: `Categoria original: "${slug}".`,
  }
}

// Formata dados dos termos para a tabela
const termosFormatados = computed(() => {
  if (!estatisticas.value?.top_termos) return []

  return estatisticas.value.top_termos.map(item => {
    const termo =
      typeof item.termo === 'string'
        ? (() => {
            try {
              return JSON.parse(item.termo)
            } catch (error) {
              console.warn('Não foi possível converter o termo de busca:', item.termo, error)
              return {}
            }
          })()
        : item.termo ?? {}

    const infoTipo = obterInfoTipo(termo.category_type)
    const infoCategoria = normalizarCategoria(termo.category_slug)

    return {
      categoria: infoCategoria.label,
      categoriaDescricao: infoCategoria.tooltip,
      tipo: infoTipo.label,
      tipoDescricao: infoTipo.tooltip,
      total: item.total,
    }
  })
})

// Dados formatados para a tabela de funil
const funilFormatado = computed(() => {
  if (!funilPorTermo.value) return []
  
  return funilPorTermo.value.map(item => {
    const termo = typeof item.termo === 'string'
        ? (() => {
            try {
              return JSON.parse(item.termo)
            } catch {
              return {}
            }
          })()
        : item.termo ?? {}

    const infoTipo = obterInfoTipo(termo.category_type)
    const infoCategoria = normalizarCategoria(termo.category_slug)

    return {
      categoria: infoCategoria.label,
      categoriaDescricao: infoCategoria.tooltip,
      tipo: infoTipo.label,
      tipoDescricao: infoTipo.tooltip,
      impressoes: item.impressoes,
      visualizacoes: item.visualizacoes,
      contatos: item.contatos,
      taxa_conversao: item.taxa_conversao,
    }
  })
})

// Carrega as estatísticas
async function carregarEstatisticas() {
  console.log('📊 [Dashboard] carregarEstatisticas() iniciada')
  console.log('📊 [Dashboard] fornecedorId.value:', fornecedorId.value)
  
  if (!fornecedorId.value) {
    console.warn('⚠️ Fornecedor ID não encontrado. Certifique-se de que um fornecedor está selecionado.')
    return
  }
  
  loading.value = true
  
  try {
    const params = calcularPeriodo()
    
    console.log('📊 [Dashboard] Buscando estatísticas para:', {
      fornecedorId: fornecedorId.value,
      params,
    })
    
    // Busca estatísticas de impressões
    const estatisticasData = await ImpressoesService.fetchEstatisticasCompletas(fornecedorId.value, params)
    console.log('📊 [Dashboard] Estatísticas recebidas:', estatisticasData)
    estatisticas.value = estatisticasData.estatisticas
    serieTemporal.value = estatisticasData.serie_temporal
    
    // Busca estatísticas de engajamento (ações)
    const engajamentoData = await AcoesService.fetchEstatisticasEngajamento(
      fornecedorId.value,
      params.data_inicio,
      params.data_fim
    )
    console.log('📊 [Dashboard] Engajamento recebido:', engajamentoData)
    estatisticasEngajamento.value = engajamentoData.estatisticas
    funilPorTermo.value = engajamentoData.funil_por_termo
  } catch (error) {
    console.error('❌ Erro ao carregar estatísticas:', error)
  } finally {
    loading.value = false
  }
}

// Calcula o período com base na seleção
function calcularPeriodo() {
  const hoje = moment()
  
  switch (periodoSelecionado.value) {
    case '7dias':
      return {
        data_inicio: hoje.clone().subtract(7, 'days').format('YYYY-MM-DD'),
        data_fim: hoje.format('YYYY-MM-DD'),
      }
    case '30dias':
      return {
        data_inicio: hoje.clone().subtract(30, 'days').format('YYYY-MM-DD'),
        data_fim: hoje.format('YYYY-MM-DD'),
      }
    case '90dias':
      return {
        data_inicio: hoje.clone().subtract(90, 'days').format('YYYY-MM-DD'),
        data_fim: hoje.format('YYYY-MM-DD'),
      }
    case 'personalizado':
      return {
        data_inicio: dataInicio.value,
        data_fim: dataFim.value,
      }
    default:
      return {}
  }
}

// Atualiza quando o período muda
function onPeriodoChange() {
  carregarEstatisticas()
}

// Carrega lista de fornecedores (para admin)
async function carregarFornecedores() {
  if (!isAdmin.value) return
  
  loadingFornecedores.value = true
  try {
    const response = await useApi.get('/admin/fornecedor')
    fornecedores.value = response.data.data || []
    
    // Se não houver fornecedor selecionado, seleciona o atual
    if (!fornecedorSelecionado.value && currentSupplier.value) {
      fornecedorSelecionado.value = currentSupplier.value
    }
  } catch (error) {
    console.error('Erro ao carregar fornecedores:', error)
  } finally {
    loadingFornecedores.value = false
  }
}

// Monitora mudanças no fornecedor selecionado
watch(fornecedorSelecionado, () => {
  if (fornecedorId.value) {
    carregarEstatisticas()
  }
})

// Monitora mudanças nos cookies (para quando carregar após refresh)
watch([currentSupplier, userData], () => {
  if (fornecedorId.value && !loading.value) {
    carregarEstatisticas()
  }
}, { deep: true })

onMounted(() => {
  if (isAdmin.value) {
    carregarFornecedores()
  }
  if (fornecedorId.value) {
    carregarEstatisticas()
  }
})
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="d-flex align-center justify-space-between flex-wrap gap-2">
            <span>Análise de Visibilidade</span>
            
            <div class="d-flex gap-2 align-center flex-wrap">
              <!-- Seletor de fornecedor (apenas para admin) -->
              <VAutocomplete
                v-if="isAdmin"
                v-model="fornecedorSelecionado"
                :items="fornecedores"
                :loading="loadingFornecedores"
                item-title="nome_fantasia"
                item-value="id"
                label="Selecione o Fornecedor"
                density="compact"
                style="min-width: 300px"
                hide-details
                return-object
                clearable
              >
                <template #item="{ props, item }">
                  <VListItem
                    v-bind="props"
                    :title="item.raw.nome_fantasia"
                    :subtitle="item.raw.razao_social"
                  />
                </template>
              </VAutocomplete>

              <VSelect
                v-model="periodoSelecionado"
                :items="[
                  { title: 'Últimos 7 dias', value: '7dias' },
                  { title: 'Últimos 30 dias', value: '30dias' },
                  { title: 'Últimos 90 dias', value: '90dias' },
                  { title: 'Personalizado', value: 'personalizado' },
                ]"
                density="compact"
                style="max-width: 200px"
                hide-details
                @update:model-value="onPeriodoChange"
              />
            </div>
          </VCardTitle>

        <VCardText v-if="periodoSelecionado === 'personalizado'" class="pt-0">
          <VRow>
            <VCol cols="12" md="6">
              <VTextField
                v-model="dataInicio"
                type="date"
                label="Data Início"
                density="compact"
                hide-details
              />
            </VCol>
            <VCol cols="12" md="6">
              <VTextField
                v-model="dataFim"
                type="date"
                label="Data Fim"
                density="compact"
                hide-details
                @change="onPeriodoChange"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Cards de estatísticas -->
    <VCol v-if="fornecedorId" cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-eye" size="20" class="me-2 text-primary" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Total de Impressões
              </h6>
              <VTooltip
                location="top"
                max-width="300"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Número de vezes que seu fornecedor apareceu nos resultados de busca.<br><br>
                  <strong>Como funciona?</strong><br>
                  Cada vez que alguém faz uma busca e você aparece na lista, conta 1 impressão (limitado a 1 por sessão).
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-primary">
              {{ estatisticas?.total || 0 }}
            </h2>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-calendar-stats" size="20" class="me-2 text-success" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Últimos 30 Dias
              </h6>
              <VTooltip
                location="top"
                max-width="300"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Impressões registradas nos últimos 30 dias.<br><br>
                  <strong>Para que serve?</strong><br>
                  Mostra a visibilidade recente do seu negócio no último mês.
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-success">
              {{ estatisticas?.ultimos_30_dias || 0 }}
            </h2>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-calendar-week" size="20" class="me-2 text-info" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Últimos 7 Dias
              </h6>
              <VTooltip
                location="top"
                max-width="300"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Impressões registradas na última semana.<br><br>
                  <strong>Para que serve?</strong><br>
                  Indica a tendência mais recente de visibilidade do seu negócio.
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-info">
              {{ estatisticas?.ultimos_7_dias || 0 }}
            </h2>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-trophy" size="20" class="me-2 text-warning" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Posição Média
              </h6>
              <VTooltip
                location="top"
                max-width="300"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Posição média em que você aparece nos resultados de busca.<br><br>
                  <strong>Como interpretar?</strong><br>
                  • Quanto MENOR, melhor (1 = primeira posição)<br>
                  • 1-3: Excelente posicionamento<br>
                  • 4-6: Bom posicionamento<br>
                  • >6: Precisa melhorar
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-warning">
              {{ estatisticas?.media_posicao || 0 }}
            </h2>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Cards de Engajamento -->
    <VCol cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-click" size="20" class="me-2 text-purple" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Visualizações de Detalhes
              </h6>
              <VTooltip
                location="top"
                max-width="300"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Quantas vezes usuários clicaram para ver mais informações sobre seu fornecedor.<br><br>
                  <strong>Quando conta?</strong><br>
                  Quando clicam no seu card nos resultados para abrir o popup com detalhes completos (1 por sessão).
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-purple">
              {{ estatisticasEngajamento?.total_visualizacoes || 0 }}
            </h2>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ estatisticasEngajamento?.visualizacoes_30_dias || 0 }} últimos 30 dias
            </div>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-phone" size="20" class="me-2 text-success" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Cliques em Contato
              </h6>
              <VTooltip
                location="top"
                max-width="300"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Quantas vezes usuários clicaram no botão "Entrar em Contato" (WhatsApp).<br><br>
                  <strong>Quando conta?</strong><br>
                  Quando clicam para te contatar via WhatsApp (1 por sessão).
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-success">
              {{ estatisticasEngajamento?.total_contatos || 0 }}
            </h2>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ estatisticasEngajamento?.contatos_30_dias || 0 }} últimos 30 dias
            </div>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-percentage" size="20" class="me-2 text-info" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Taxa de Visualização
              </h6>
              <VTooltip
                location="top"
                max-width="350"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Percentual de pessoas que clicaram para ver detalhes após te verem nos resultados.<br><br>
                  <strong>Fórmula:</strong><br>
                  (Visualizações ÷ Impressões) × 100<br><br>
                  <strong>Benchmarks:</strong><br>
                  • &lt;10%: Ruim<br>
                  • 10-30%: Médio<br>
                  • 30-50%: Bom<br>
                  • &gt;50%: Excelente
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-info">
              {{ estatisticasEngajamento?.taxa_visualizacao || 0 }}%
            </h2>
            <div class="text-caption text-medium-emphasis mt-1">
              Visualizações / Impressões
            </div>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12" md="3">
      <VCard>
        <VCardText>
          <div class="text-center">
            <div class="d-flex align-center justify-center mb-2">
              <VIcon icon="tabler-chart-line" size="20" class="me-2 text-error" />
              <h6 class="text-h6 text-medium-emphasis mb-0">
                Taxa de Conversão
              </h6>
              <VTooltip
                location="top"
                max-width="350"
              >
                <template #activator="{ props }">
                  <VIcon
                    v-bind="props"
                    icon="tabler-info-circle"
                    size="18"
                    class="ms-1 text-medium-emphasis cursor-pointer"
                  />
                </template>
                <div class="pa-2">
                  <strong>O que é?</strong><br>
                  Percentual de pessoas que entraram em contato após te verem nos resultados.<br><br>
                  <strong>Fórmula:</strong><br>
                  (Contatos ÷ Impressões) × 100<br><br>
                  <strong>Benchmarks:</strong><br>
                  • &lt;5%: Ruim<br>
                  • 5-15%: Médio<br>
                  • 15-25%: Bom<br>
                  • &gt;25%: Excelente
                </div>
              </VTooltip>
            </div>
            <h2 class="text-h2 font-weight-bold text-error">
              {{ estatisticasEngajamento?.taxa_conversao || 0 }}%
            </h2>
            <div class="text-caption text-medium-emphasis mt-1">
              Contatos / Impressões
            </div>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Gráfico de linha -->
    <VCol v-if="fornecedorId" cols="12">
      <VCard>
        <VCardTitle>Evolução das Impressões</VCardTitle>
        <VCardText>
          <!-- Debug: mostra quantos dados temos -->
          <div v-if="!loading" class="text-caption mb-2 text-medium-emphasis">
            {{ serieTemporal.length }} registros encontrados
          </div>
          
          <VueApexCharts
            v-if="!loading && serieTemporal.length > 0"
            type="line"
            height="350"
            :options="chartOptions"
            :series="chartSeries"
          />
          <div v-else-if="loading" class="text-center py-8">
            <VProgressCircular indeterminate color="primary" />
          </div>
          <div v-else class="text-center py-8 text-medium-emphasis">
            <VIcon icon="tabler-chart-line" size="64" class="mb-4" />
            <p>Nenhum dado disponível para o período selecionado</p>
            <p class="text-caption">Faça buscas na plataforma para gerar impressões</p>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Gráfico de Funil de Conversão -->
    <VCol v-if="fornecedorId && estatisticasEngajamento" cols="12">
      <VCard>
        <VCardTitle>Funil de Conversão ao Longo do Tempo</VCardTitle>
        <VCardText>
          <VueApexCharts
            v-if="!loading && serieTemporal.length > 0"
            type="line"
            height="350"
            :options="chartFunilOptions"
            :series="chartFunilSeries"
          />
          <div v-else-if="loading" class="text-center py-8">
            <VProgressCircular indeterminate color="primary" />
          </div>
          <div v-else class="text-center py-8 text-medium-emphasis">
            <VIcon icon="tabler-funnel" size="64" class="mb-4" />
            <p>Nenhum dado disponível para o período selecionado</p>
            <p class="text-caption">O funil mostra a progressão de impressões → visualizações → contatos</p>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Top Termos de Busca -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>Top Termos de Busca</VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headersTermos"
            :items="termosFormatados"
            :loading="loading"
            density="compact"
            hide-default-footer
            items-per-page="10"
          >
            <template #header.categoria="{ column }">
              <div class="d-inline-flex align-center gap-1">
                <span>{{ column.title }}</span>
                <VTooltip
                  location="top"
                  :text="tooltipCategoriaResumo"
                >
                  <template #activator="{ props }">
                    <VIcon
                      v-bind="props"
                      size="16"
                      icon="tabler-info-circle"
                      class="text-medium-emphasis cursor-pointer"
                    />
                  </template>
                </VTooltip>
              </div>
            </template>

            <template #item.categoria="{ item }">
              <VTooltip
                v-if="item.categoriaDescricao"
                location="top"
                :text="item.categoriaDescricao"
              >
                <template #activator="{ props }">
                  <span v-bind="props">{{ item.categoria }}</span>
                </template>
              </VTooltip>
              <span v-else>{{ item.categoria }}</span>
            </template>

            <template #header.tipo="{ column }">
              <div class="d-inline-flex align-center gap-1">
                <span>{{ column.title }}</span>
                <VTooltip
                  location="top"
                  :text="tooltipTipoResumo"
                >
                  <template #activator="{ props }">
                    <VIcon
                      v-bind="props"
                      size="16"
                      icon="tabler-info-circle"
                      class="text-medium-emphasis cursor-pointer"
                    />
                  </template>
                </VTooltip>
              </div>
            </template>

            <template #item.tipo="{ item }">
              <VTooltip
                v-if="item.tipoDescricao"
                location="top"
                :text="item.tipoDescricao"
              >
                <template #activator="{ props }">
                  <span v-bind="props">{{ item.tipo }}</span>
                </template>
              </VTooltip>
              <span v-else>{{ item.tipo }}</span>
            </template>

            <template #no-data>
              <div class="text-center py-4 text-medium-emphasis">
                Nenhum dado disponível
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Funil de Conversão por Termo de Busca -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>Funil de Conversão por Termo de Busca</VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headersFunil"
            :items="funilFormatado"
            :loading="loading"
            density="compact"
            hide-default-footer
            items-per-page="10"
          >
            <template #header.categoria="{ column }">
              <div class="d-inline-flex align-center gap-1">
                <span>{{ column.title }}</span>
                <VTooltip
                  location="top"
                  :text="tooltipCategoriaResumo"
                >
                  <template #activator="{ props }">
                    <VIcon
                      v-bind="props"
                      size="16"
                      icon="tabler-info-circle"
                      class="text-medium-emphasis cursor-pointer"
                    />
                  </template>
                </VTooltip>
              </div>
            </template>

            <template #item.categoria="{ item }">
              <VTooltip
                v-if="item.categoriaDescricao"
                location="top"
                :text="item.categoriaDescricao"
              >
                <template #activator="{ props }">
                  <span v-bind="props">{{ item.categoria }}</span>
                </template>
              </VTooltip>
              <span v-else>{{ item.categoria }}</span>
            </template>

            <template #header.tipo="{ column }">
              <div class="d-inline-flex align-center gap-1">
                <span>{{ column.title }}</span>
                <VTooltip
                  location="top"
                  :text="tooltipTipoResumo"
                >
                  <template #activator="{ props }">
                    <VIcon
                      v-bind="props"
                      size="16"
                      icon="tabler-info-circle"
                      class="text-medium-emphasis cursor-pointer"
                    />
                  </template>
                </VTooltip>
              </div>
            </template>

            <template #item.tipo="{ item }">
              <VTooltip
                v-if="item.tipoDescricao"
                location="top"
                :text="item.tipoDescricao"
              >
                <template #activator="{ props }">
                  <span v-bind="props">{{ item.tipo }}</span>
                </template>
              </VTooltip>
              <span v-else>{{ item.tipo }}</span>
            </template>

            <template #item.taxa_conversao="{ item }">
              <span class="font-weight-bold" :class="{
                'text-success': item.taxa_conversao >= 5,
                'text-warning': item.taxa_conversao >= 2 && item.taxa_conversao < 5,
                'text-error': item.taxa_conversao < 2
              }">
                {{ item.taxa_conversao }}%
              </span>
            </template>

            <template #no-data>
              <div class="text-center py-4 text-medium-emphasis">
                Nenhum dado disponível
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Mensagem quando nenhum fornecedor está selecionado -->
    <VCol v-if="!fornecedorId" cols="12">
      <VCard>
        <VCardText class="text-center py-12">
          <VIcon icon="tabler-chart-line" size="64" class="mb-4 text-medium-emphasis" />
          <h3 class="text-h5 mb-2">Selecione um Fornecedor</h3>
          <p class="text-medium-emphasis">
            {{ isAdmin ? 'Escolha um fornecedor acima para visualizar suas estatísticas' : 'Nenhum fornecedor encontrado' }}
          </p>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
  </div>
</template>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>

